<script lang="ts" setup>
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import {
    CAPTCHA_BYPASS_LOCATIONS,
    TURNSTILE_INVISIBLE_SITE_KEY,
    TURNSTILE_INVISIBLE_TEST_SITE_KEY,
    TURNSTILE_TYPE,
    TURNSTILE_VISIBLE_SITE_KEY,
    TURNSTILE_VISIBLE_TEST_SITE_KEY,
    type TurnstileAction,
    type TurnstileType,
  } from '@/utils/const'
  import { useStore } from '@/utils/store'

  declare global {
    interface Window {
      turnstile?: {
        render: (container: HTMLElement, options: Record<string, unknown>) => string
        remove: (widgetId: string) => void
        reset: (widgetId: string) => void
      }
    }
  }

  // How long to wait for the Cloudflare api.js script before treating it as
  // unavailable. Ad-blockers / locked-down networks silently block this URL;
  // without a timeout the widget hangs indefinitely with no UI feedback.
  const SCRIPT_LOAD_TIMEOUT_MS = 10000

  const props = defineProps<{ mode: TurnstileType; action?: TurnstileAction }>()
  const emit = defineEmits<{
    (e: 'verify', payload: { token: string }): void
    (e: 'expired'): void
    (e: 'error', error: unknown): void
  }>()

  const store = useStore()
  const turnstileContainer = ref<HTMLElement | null>(null)
  const widgetId = ref<string | undefined>()

  const onVerify = (token: string): void => {
    emit('verify', { token })
  }
  const onExpired = (): void => {
    emit('expired')
  }
  const onError = (error: unknown): void => {
    // Cloudflare's runtime error-callback fires for expired tokens, invalid
    // sitekey config, network blips mid-render, etc. Emit so the parent can
    // surface feedback and clear any stale token — without this, the user
    // sees a silently-dead widget with submit still enabled.
    console.error('Turnstile widget error:', error)
    emit('error', error)
  }

  let loadPromise: Promise<void> | null = null
  const loadTurnstile = (): Promise<void> => {
    if (loadPromise) return loadPromise
    loadPromise = new Promise<void>((resolve, reject) => {
      if (window.turnstile) {
        resolve()
        return
      }
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      const timer = window.setTimeout(() => {
        loadPromise = null
        reject(new Error('Turnstile script load timed out'))
      }, SCRIPT_LOAD_TIMEOUT_MS)
      script.onload = () => {
        window.clearTimeout(timer)
        resolve()
      }
      script.onerror = () => {
        window.clearTimeout(timer)
        loadPromise = null
        reject(new Error('Turnstile script load failed'))
      }
      document.head.appendChild(script)
    })
    return loadPromise
  }

  const renderTurnstile = (siteKey: string): void => {
    if (widgetId.value) {
      window.turnstile?.remove(widgetId.value)
      widgetId.value = undefined
    }
    if (!window.turnstile || !turnstileContainer.value) return
    // Cloudflare's `size` accepts only 'compact' | 'flexible' | 'normal' — invisible mode is
    // selected by the SITE KEY type configured in the Cloudflare dashboard, not by `size`.
    const renderOptions: Record<string, unknown> = {
      sitekey: siteKey,
      callback: onVerify,
      'error-callback': onError,
      'expired-callback': onExpired,
    }
    // `action` is the endpoint-scoped label Cloudflare attaches to the token
    // so server-side siteverify can validate token provenance per surface
    // (e.g. `ecomm-cac-login`). Skipped when not provided so the widget still
    // works without an explicit action.
    if (props.action) {
      renderOptions.action = props.action
    }
    if (props.mode === TURNSTILE_TYPE.NORMAL) {
      renderOptions.size = 'normal'
    }
    widgetId.value = window.turnstile.render(turnstileContainer.value, renderOptions)
  }

  const resolveSiteKey = (): string => {
    // Backend bypasses captcha for these locationIds via `isWhitelistedLocations`; pair that with
    // Cloudflare's always-pass dummy sitekeys so the widget doesn't render a real challenge for them.
    const isCaptchaBypass = CAPTCHA_BYPASS_LOCATIONS.includes(store.locationId.value)
    if (props.mode === TURNSTILE_TYPE.NORMAL) {
      return isCaptchaBypass ? TURNSTILE_VISIBLE_TEST_SITE_KEY : TURNSTILE_VISIBLE_SITE_KEY
    }
    return isCaptchaBypass ? TURNSTILE_INVISIBLE_TEST_SITE_KEY : TURNSTILE_INVISIBLE_SITE_KEY
  }

  const renderForCurrentMode = (): void => {
    renderTurnstile(resolveSiteKey())
  }

  // Cloudflare Turnstile tokens are single-use and expire in ~300s. After
  // each submit the parent calls `reset()` to invalidate the current token
  // and trigger a fresh challenge so the next submit isn't replaying a
  // stale/consumed token.
  const reset = (): void => {
    if (widgetId.value && window.turnstile) {
      window.turnstile.reset(widgetId.value)
    } else {
      renderForCurrentMode()
    }
  }

  defineExpose({ renderTurnstile, renderForCurrentMode, reset })

  // Re-render whenever the parent flips between invisible and normal so the
  // visible challenge appears immediately on the first escalation — without
  // relying on the parent to imperatively call `renderForCurrentMode()`.
  watch(
    () => props.mode,
    () => {
      if (window.turnstile) renderForCurrentMode()
    }
  )

  onMounted(() => {
    if (window.turnstile) {
      renderForCurrentMode()
    } else {
      loadTurnstile()
        .then(() => renderForCurrentMode())
        .catch((err) => {
          // Most common cause: ad-blocker / locked-down network blocked the
          // api.js request, or it timed out. Surface this to the parent so
          // it can show a "verification unavailable" message rather than
          // leaving the form silently un-submittable.
          console.error(err)
          emit('error', err)
        })
    }
  })

  onUnmounted(() => {
    if (widgetId.value) {
      window.turnstile?.remove(widgetId.value)
      widgetId.value = undefined
    }
  })
</script>

<template>
  <div ref="turnstileContainer"></div>
</template>
